/**
 * React Query bindings for the BYOK key manager.
 *
 * Two invariants live here.
 *
 * **The plaintext key is never cached.** Mutations take it as an argument and
 * return the server's masked row; nothing writes it to a query cache, to
 * storage, or to a module-level variable. `useSaveByokKey` deliberately has no
 * `onSuccess` payload transform that could re-expose the input.
 *
 * **Every mutation invalidates the paywall cache too.** `["payment-config"]`
 * is the single key every `PaywallGate` / `AIFeatureGate` reads from. Adding a
 * key unlocks gated surfaces server-side immediately; leaving that cache stale
 * would leave the user staring at an unlock CTA for a feature that already
 * works.
 *
 * @module ai_services/hooks/use-byok
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { paywallKeys } from "@shared/hooks/use-paywall";
import { modelPreferenceKeys } from "./use-model-preferences";
import {
  deleteByokKey,
  getByokStatus,
  getByokUsage,
  listByokKeys,
  saveByokKey,
  testByokKey,
} from "../api";
import type {
  ByokKey,
  ByokKeyListResponse,
  ByokProvider,
  ByokStatus,
  ByokUsageRange,
  ByokUsageSummary,
  SaveByokKeyRequest,
} from "../api";

export const byokKeys = {
  all: ["byok"] as const,
  keys: () => ["byok", "keys"] as const,
  status: () => ["byok", "status"] as const,
  usage: (since?: string, until?: string) =>
    ["byok", "usage", since, until] as const,
};

/**
 * Invalidate everything a key change can affect: the BYOK tree and the shared
 * paywall snapshot that gates the features the key just unlocked.
 */
export function invalidateAfterKeyChange(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: byokKeys.all });
  void queryClient.invalidateQueries({ queryKey: modelPreferenceKeys.all });
  void queryClient.invalidateQueries({ queryKey: paywallKeys.all });
}

export function useByokKeys() {
  return useQuery<ByokKeyListResponse>({
    queryKey: byokKeys.keys(),
    queryFn: listByokKeys,
    staleTime: 30_000,
  });
}

/**
 * `staleTime: 0` — this is the authority for what is unlocked, and a stale
 * answer here is the difference between a working feature and a locked one.
 */
export function useByokStatus() {
  return useQuery<ByokStatus>({
    queryKey: byokKeys.status(),
    queryFn: getByokStatus,
    staleTime: 0,
  });
}

export function useByokUsage(range: ByokUsageRange = {}) {
  return useQuery<ByokUsageSummary>({
    queryKey: byokKeys.usage(range.since, range.until),
    queryFn: () => getByokUsage(range),
    staleTime: 60_000,
  });
}

export function useSaveByokKey() {
  const queryClient = useQueryClient();
  return useMutation<ByokKey, Error, SaveByokKeyRequest>({
    mutationKey: ["byok", "save"],
    mutationFn: saveByokKey,
    onSuccess: () => invalidateAfterKeyChange(queryClient),
  });
}

export function useTestByokKey() {
  const queryClient = useQueryClient();
  return useMutation<ByokKey, Error, ByokProvider>({
    mutationKey: ["byok", "test"],
    mutationFn: testByokKey,
    // A re-test can flip `is_validated` either way, so the gates move with it.
    onSettled: () => invalidateAfterKeyChange(queryClient),
  });
}

export function useDeleteByokKey() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, ByokProvider>({
    mutationKey: ["byok", "delete"],
    mutationFn: deleteByokKey,
    onSuccess: () => invalidateAfterKeyChange(queryClient),
  });
}
